import { call, put, select } from 'typed-redux-saga';

import { setCustomTheme, setIsOneOnOne } from '../../../shared/features/config';
import { selectIsTeleMessengerConferencesHistory } from '../../../shared/features/config/selectors';
import {
    setNoiseCancellationTypeAction,
    setVirtualBackgroundType,
    toggleCameraFacingModeAction,
} from '../../../shared/features/settings';
import { itemRatioHash } from '../../../shared/features/settings/sagas';
import {
    setHideParticipantsVideoAction,
    setStoreShowMyVideoSettingsAction,
} from '../../../shared/features/settings/slice';
import { getAvatarPlaceholder } from '../../../shared/helpers/avatar-placeholder';
import { createLogger } from '../../../shared/helpers/loggerFacade';
import { RolePermission } from '../../../shared/helpers/permissions';
import { VIRTUAL_CALLEE } from '../constants';
import { setChatUnreadCount } from '../features/chat';
import { cancelCallAction } from '../features/conference/slice';
import { toggleMuteAction } from '../features/device';
import { setIsFullscreenAction } from '../features/fullscreen';
import { setStoreGridItemRatio } from '../features/goloom-video-grid';
import {
    isRecordingForMembersAllowedSelector,
    selectHasMembersCalendarSummarizationReceivePermission,
    selectHasMembersCloudRecordingPermission,
    selectHasMembersSummarizationPermission,
} from '../features/permissions';
import {
    setCalendarSummarizationForMembersAllowed,
    setCloudRecordingForMembersAllowed,
    setRecordingForMembersAllowed,
    setSummarizationForMembersAllowed,
} from '../features/permissions/slice';
import { shareScreenAction } from '../features/screen-sharing/slice';
import {
    selectIsSummarizationMailForParticipants,
    setIsSummarizationMailForParticipantsAction,
} from '../features/summarization';
import { selectParticipants } from '../selectors';
import { ParticipantState } from '../types';
import { handleCreateBroadcastMessage } from './handle-create-broadcast-message';
import {
    acceptCallAction,
    screenRecordingAction,
    setBackButtonVisible,
    setOutgoingCallCallee,
    setOutgoingCallStatus,
    syncMediaDeviceAction,
} from './slice';
import { IncomingEvents, IncomingPostMessage } from './types';

const logger = createLogger('handleTeleMessengerMessage');

function* setOutgoingCallCalleeSaga(participantState: Maybe<ParticipantState>) {
    const participants = yield* select(selectParticipants);
    if (participants.length >= 2) {
        return;
    }
    yield* put(setOutgoingCallCallee(participantState));
}
export function* handleTeleMessengerMessage(message: IncomingPostMessage) {
    switch (message.event) {
        case IncomingEvents.SET_MUTE: {
            yield* put(toggleMuteAction(message.payload));
            break;
        }

        case IncomingEvents.SET_GRID_ITEM_RATIO: {
            const gridItemRatio = itemRatioHash[message.payload.value];

            if (gridItemRatio) {
                yield* put(setStoreGridItemRatio({ gridItemRatio }));
            }
            break;
        }

        case IncomingEvents.CANCEL_CALL: {
            yield* put(cancelCallAction());
            break;
        }

        case IncomingEvents.SHARE_SCREEN: {
            yield* put(shareScreenAction(message.payload.value));
            break;
        }

        case IncomingEvents.UNREAD_COUNT: {
            yield* put(setChatUnreadCount(message.payload.value));
            break;
        }

        case IncomingEvents.SET_MEDIA_DEVICE: {
            yield* put(syncMediaDeviceAction(message.payload));

            break;
        }

        case IncomingEvents.SET_CALLEE_PARTICIPANT: {
            yield* call(setOutgoingCallCalleeSaga, {
                id: VIRTUAL_CALLEE,
                displayName: message.payload.displayName,
                avatarUrl: message.payload.avatarUrl,
                avatarPlaceholder: getAvatarPlaceholder({ name: message.payload.displayName }),
            });

            break;
        }

        case IncomingEvents.SET_CALL_STATUS: {
            yield* put(setOutgoingCallStatus(message.payload));

            break;
        }

        case IncomingEvents.SET_IS_ONE_ON_ONE: {
            yield* put(setIsOneOnOne(message.payload.value));

            break;
        }

        case IncomingEvents.CLOSE_FULL_SCREEN: {
            yield* put(setIsFullscreenAction(false));

            break;
        }

        case IncomingEvents.ACCEPT_CALL: {
            yield* put(acceptCallAction());

            break;
        }

        case IncomingEvents.CREATE_BROADCAST: {
            yield* handleCreateBroadcastMessage(message);

            break;
        }

        case IncomingEvents.START_SCREEN_RECORDING_RESULT:
        case IncomingEvents.ADD_SCREEN_RECORDING_CHUNK_RESULT:
        case IncomingEvents.END_SCREEN_RECORDING_RESULT: {
            yield* put(screenRecordingAction(message));

            break;
        }

        case IncomingEvents.SET_NOISE_CANCELLATION: {
            yield* put(setNoiseCancellationTypeAction(message.payload.type));
            break;
        }
        case IncomingEvents.SET_VIRTUAL_BACKGROUND: {
            yield* put(setVirtualBackgroundType(message.payload.type));
            break;
        }

        case IncomingEvents.SET_CONFERENCE_PERMISSION: {
            const { permissionName, value } = message.payload;

            switch (permissionName) {
                case RolePermission.RECORDING: {
                    const currentPermissionValue = yield* select(isRecordingForMembersAllowedSelector);

                    if (currentPermissionValue !== value) {
                        yield* put(setRecordingForMembersAllowed(value));
                    }

                    break;
                }
                case RolePermission.CLOUD_RECORDING: {
                    const currentPermissionValue = yield* select(selectHasMembersCloudRecordingPermission);
                    if (currentPermissionValue !== value) {
                        yield* put(setCloudRecordingForMembersAllowed(value));
                    }

                    break;
                }
                case RolePermission.SUMMARIZATION: {
                    const currentPermissionValue = yield* select(selectHasMembersSummarizationPermission);
                    if (currentPermissionValue !== value) {
                        yield* put(setSummarizationForMembersAllowed(value));
                    }
                    break;
                }
                case RolePermission.CALENDAR_SUMMARIZATION_RECEIVE: {
                    const currentPermissionValue = yield* select(
                        selectHasMembersCalendarSummarizationReceivePermission
                    );
                    if (currentPermissionValue !== value) {
                        yield* put(setCalendarSummarizationForMembersAllowed(value));
                    }

                    break;
                }
            }
            break;
        }
        case IncomingEvents.SET_SUMMARIZATION_MAIL_FOR_PARTICIPANTS: {
            const { value } = message.payload;
            const currentSummarizationMail = yield* select(selectIsSummarizationMailForParticipants);
            if (currentSummarizationMail !== value) {
                yield* put(setIsSummarizationMailForParticipantsAction(value));
            }
            break;
        }
        case IncomingEvents.SET_SHOW_MY_VIDEO: {
            yield* put(setStoreShowMyVideoSettingsAction(message.payload.value));
            break;
        }
        case IncomingEvents.SET_HIDE_PARTICIPANTS_VIDEO: {
            yield* put(setHideParticipantsVideoAction(message.payload.value));
            break;
        }
        case IncomingEvents.SET_VIDEO_FACING_MODE: {
            yield* put(toggleCameraFacingModeAction(message.payload.value));
            break;
        }
        case IncomingEvents.SET_BACK_BUTTON_VISIBILITY: {
            yield* put(setBackButtonVisible(message.payload.value));
            break;
        }
        case IncomingEvents.SET_THEME: {
            const isConferencesHistoryPage = yield* select(selectIsTeleMessengerConferencesHistory);
            if (isConferencesHistoryPage) {
                yield* put(setCustomTheme(message.payload.theme));
            }

            break;
        }
        default: {
            logger.warn('Unhandled telemessenger message event', {
                method: 'handleTeleMessengerMessage',
                additional: {
                    message: message.event,
                },
            });
            break;
        }
    }
}
